import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ChevronDownIcon, DocumentArrowDownIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { API_URL } from "../../apiConfig";

const T = {
  ink:        "#1A1A1C",
  ink2:       "#4A4A50",
  ink3:       "#86868B",
  ink4:       "#B4B3B0",
  hair:       "rgba(20,20,22,0.08)",
  surface2:   "#f9f8f6",
  accent:     "#1e3a8a",
  accentSoft: "rgba(30,58,138,0.09)",
  font:       "-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
};

const REPORT_STATUSES = [
  { value: "ATIVADO",   label: "Ativado",   color: "#1E8E3E", rgb: [30, 142, 62] },
  { value: "PAGO",      label: "Pago",      color: "#0F766E", rgb: [15, 118, 110] },
  { value: "PENDENTE",  label: "Pendente",  color: "#B25E09", rgb: [178, 94, 9] },
  { value: "NEGATIVA",  label: "Negativa",  color: "#C5221F", rgb: [197, 34, 31] },
  { value: "INATIVADO", label: "Inativado", color: "#86868B", rgb: [134, 134, 139] },
];
const DEFAULT_STATUSES = ["ATIVADO", "PAGO", "PENDENTE"];
const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

const cents = (value) => Number(String(value || "").replace(/[^0-9]/g, "")) || 0;
const money = (c) => `R$ ${(c / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateOnly = (d) => {
  if (!d) return "—";
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d.split("-").reverse().join("/");
  return new Date(d).toLocaleDateString("pt-BR");
};
const titleCase = (name = "") => name.toLowerCase().trim().replace(/(^|\s)\S/g, (c) => c.toUpperCase()) || "—";
const condoOf = (a) => a.condo || a.condoName || "";

function reportStatus(a) {
  if (a._negativeDoc) return "NEGATIVA";
  const s = String(a.status || "PENDENTE").toUpperCase();
  if (s === "NEGATIVA") return "NEGATIVA";
  if (a.disabledAt) return "INATIVADO";
  if (s === "ATIVADO") return a.paidAt ? "PAGO" : "ATIVADO";
  return "PENDENTE";
}

function periodRange(period) {
  if (period.mode === "month") {
    const [y, m] = period.month.split("-").map(Number);
    if (!y || !m) return null;
    return { start: new Date(Date.UTC(y, m - 1, 1)), end: new Date(Date.UTC(y, m, 0, 23, 59, 59, 999)), label: `${MONTHS[m - 1]} de ${y}` };
  }
  if (period.mode === "year") {
    const y = Number(period.year);
    if (!y) return null;
    return { start: new Date(Date.UTC(y, 0, 1)), end: new Date(Date.UTC(y, 11, 31, 23, 59, 59, 999)), label: `ano de ${y}` };
  }
  if (!period.start || !period.end) return null;
  return {
    start: new Date(`${period.start}T00:00:00.000Z`),
    end: new Date(`${period.end}T23:59:59.999Z`),
    label: `${dateOnly(period.start)} a ${dateOnly(period.end)}`,
  };
}

function countBy(list, keyOf) {
  const counts = {};
  list.forEach((item) => { const k = keyOf(item); if (k) counts[k] = (counts[k] || 0) + 1; });
  return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0], "pt-BR"));
}

function toggle(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

// Campo de seleção múltipla com busca (vazio = todos)
function MultiSelect({ options, selected, onChange, allLabel, searchPlaceholder, emptyText, loading }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey, true); };
  }, [open]);
  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);
  const colorOf = (value) => options.find((o) => o.value === value)?.color;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
        style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minHeight: 40, padding: "5px 36px 5px 10px", borderRadius: 10, border: `1px solid ${open ? T.accent : T.hair}`, boxShadow: open ? "0 0 0 3px rgba(30,58,138,.10)" : "none", background: "#fff", cursor: "pointer", position: "relative", fontFamily: T.font }}
      >
        {selected.length === 0 && <span style={{ fontSize: 13, color: T.ink2, padding: "3px 2px" }}>{allLabel}</span>}
        {selected.slice(0, 4).map((value) => (
          <span key={value} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 4px 3px 9px", borderRadius: 999, fontSize: 12, background: T.accentSoft, color: T.accent, maxWidth: 240 }}>
            {colorOf(value) && <span style={{ width: 7, height: 7, borderRadius: "50%", background: colorOf(value), flexShrink: 0 }} />}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</span>
            <button type="button" aria-label={`Remover ${value}`} onClick={(e) => { e.stopPropagation(); onChange(selected.filter((v) => v !== value)); }} style={{ background: "none", border: "none", padding: 0, display: "flex", color: T.accent, cursor: "pointer" }}>
              <XMarkIcon style={{ width: 13, height: 13 }} />
            </button>
          </span>
        ))}
        {selected.length > 4 && <span style={{ fontSize: 12, color: T.ink3 }}>+{selected.length - 4}</span>}
        <ChevronDownIcon style={{ position: "absolute", right: 12, top: "50%", transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, width: 16, height: 16, color: T.ink3, transition: "transform .15s" }} />
      </div>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 20, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 12, boxShadow: "0 12px 32px -8px rgba(20,20,22,0.18)", overflow: "hidden" }}>
          <div style={{ position: "relative", borderBottom: `1px solid ${T.hair}` }}>
            <MagnifyingGlassIcon style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 15, height: 15, color: T.ink3 }} />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={searchPlaceholder} style={{ width: "100%", height: 38, border: "none", outline: "none", fontSize: 13, padding: "0 12px 0 34px", fontFamily: T.font }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 12px", borderBottom: `1px solid ${T.hair}`, fontSize: 11.5, color: T.ink3 }}>
            <span>{selected.length ? `${selected.length} selecionado${selected.length > 1 ? "s" : ""}` : allLabel}</span>
            {selected.length > 0 && (
              <button type="button" onClick={() => onChange([])} style={{ background: "none", border: "none", padding: 0, fontSize: 11.5, color: T.accent, cursor: "pointer", fontFamily: T.font }}>Limpar seleção</button>
            )}
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {loading && <div style={{ padding: 14, fontSize: 12.5, color: T.ink3, textAlign: "center" }}><Spinner size="sm" animation="border" /> Carregando…</div>}
            {!loading && visible.length === 0 && <div style={{ padding: 14, fontSize: 12.5, color: T.ink4, textAlign: "center" }}>{query ? "Nada encontrado" : emptyText}</div>}
            {!loading && visible.map((o) => {
              const on = selected.includes(o.value);
              return (
                <label key={o.value} className="rep-opt" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", fontSize: 13, cursor: "pointer", background: on ? T.accentSoft : "transparent", color: T.ink, margin: 0 }}>
                  <input type="checkbox" checked={on} onChange={() => onChange(toggle(selected, o.value))} />
                  {o.color && <span style={{ width: 8, height: 8, borderRadius: "50%", background: o.color, flexShrink: 0 }} />}
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                  <span style={{ fontSize: 11.5, color: T.ink3 }}>{o.count}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function buildPdf({ rows, filtersText, generatedBy }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 36;

  // Cabeçalho
  doc.setFillColor(30, 58, 138);
  doc.rect(0, 0, W, 64, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold").setFontSize(17).text("Relatório de Ativações", M, 30);
  doc.setFont("helvetica", "normal").setFontSize(9.5).text("Euro Privilege", M, 46);
  doc.setFontSize(8.5).text(`Gerado em ${new Date().toLocaleString("pt-BR")}${generatedBy ? ` por ${generatedBy}` : ""}`, W - M, 46, { align: "right" });

  doc.setTextColor(74, 74, 80).setFontSize(8.5);
  doc.text(doc.splitTextToSize(filtersText, W - 2 * M), M, 84);

  // Resumo
  const byStatus = (s) => rows.filter((r) => r.status === s);
  const sum = (list) => list.reduce((t, r) => t + r.valueCents, 0);
  const ativados = byStatus("ATIVADO");
  const pagos = byStatus("PAGO");
  const cards = [
    { label: "Registros", value: String(rows.length), sub: "no relatório" },
    { label: "Ativas", value: String(ativados.length + pagos.length), sub: money(sum(ativados) + sum(pagos)), rgb: [30, 142, 62] },
    { label: "Pagas", value: String(pagos.length), sub: money(sum(pagos)), rgb: [15, 118, 110] },
    { label: "A receber", value: String(ativados.length), sub: money(sum(ativados)), rgb: [30, 58, 138] },
    { label: "Pendentes", value: String(byStatus("PENDENTE").length), sub: money(sum(byStatus("PENDENTE"))), rgb: [178, 94, 9] },
    { label: "Negativas", value: String(byStatus("NEGATIVA").length), sub: "", rgb: [197, 34, 31] },
  ];
  const gap = 8;
  const cw = (W - 2 * M - gap * (cards.length - 1)) / cards.length;
  const cy = 100;
  cards.forEach((c, i) => {
    const x = M + i * (cw + gap);
    doc.setDrawColor(225, 225, 228).setFillColor(249, 248, 246).roundedRect(x, cy, cw, 52, 6, 6, "FD");
    doc.setTextColor(134, 134, 139).setFont("helvetica", "normal").setFontSize(7.5).text(c.label.toUpperCase(), x + 10, cy + 15);
    doc.setTextColor(...(c.rgb || [26, 26, 28])).setFont("helvetica", "bold").setFontSize(16).text(c.value, x + 10, cy + 35);
    if (c.sub) doc.setTextColor(74, 74, 80).setFont("helvetica", "normal").setFontSize(8).text(c.sub, x + cw - 10, cy + 35, { align: "right" });
  });

  // Resumo por parceiro
  const partners = {};
  rows.forEach((r) => {
    const p = (partners[r.provider || "—"] ||= { ATIVADO: 0, PAGO: 0, PENDENTE: 0, NEGATIVA: 0, INATIVADO: 0, active: 0 });
    p[r.status] += 1;
    if (r.status === "ATIVADO" || r.status === "PAGO") p.active += r.valueCents;
  });
  autoTable(doc, {
    startY: cy + 70,
    margin: { left: M, right: M },
    head: [["Parceiro", "Ativados", "Pagos", "Pendentes", "Negativas", "Inativados", "Valor ativo"]],
    body: Object.entries(partners)
      .sort((a, b) => b[1].active - a[1].active)
      .map(([name, p]) => [name, p.ATIVADO, p.PAGO, p.PENDENTE, p.NEGATIVA, p.INATIVADO, money(p.active)]),
    styles: { fontSize: 8, cellPadding: 4, textColor: [26, 26, 28] },
    headStyles: { fillColor: [241, 240, 237], textColor: [74, 74, 80], fontStyle: "bold" },
    columnStyles: { 1: { halign: "center" }, 2: { halign: "center" }, 3: { halign: "center" }, 4: { halign: "center" }, 5: { halign: "center" }, 6: { halign: "right", fontStyle: "bold" } },
    theme: "grid",
  });

  // Lista detalhada
  const statusMeta = Object.fromEntries(REPORT_STATUSES.map((s) => [s.value, s]));
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 18,
    margin: { left: M, right: M, bottom: 34 },
    head: [["Cliente", "Documento", "Parceiro", "Plano", "Condomínio", "Valor", "Ativação", "Instalação", "Status"]],
    body: rows.map((r) => [r.client, r.document, r.provider || "—", r.plan || "—", r.condo || "—", r.valueCents ? money(r.valueCents) : "—", r.activation, r.installation, statusMeta[r.status]?.label || r.status]),
    styles: { fontSize: 7.8, cellPadding: 3.5, textColor: [26, 26, 28], overflow: "linebreak" },
    headStyles: { fillColor: [30, 58, 138], textColor: [255, 255, 255], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [249, 248, 246] },
    columnStyles: { 0: { cellWidth: 130 }, 5: { halign: "right" }, 8: { fontStyle: "bold" } },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 8) {
        const meta = REPORT_STATUSES.find((s) => s.label === data.cell.raw);
        if (meta) data.cell.styles.textColor = meta.rgb;
      }
    },
  });

  // Rodapé com paginação
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    const H = doc.internal.pageSize.getHeight();
    doc.setTextColor(134, 134, 139).setFontSize(7.5).setFont("helvetica", "normal");
    doc.text("Euro Privilege · Relatório de Ativações · uso interno", M, H - 16);
    doc.text(`Página ${i} de ${pages}`, W - M, H - 16, { align: "right" });
  }
  return doc;
}

export default function ActivationsReportModal({ show, onHide, partnerColorMap = {}, defaultPeriod, notify }) {
  const [period, setPeriod] = useState(defaultPeriod);
  const [selProviders, setSelProviders] = useState([]);
  const [selStatuses, setSelStatuses] = useState(DEFAULT_STATUSES);
  const [selCondos, setSelCondos] = useState([]);
  const [source, setSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  // notify muda a cada render do pai; ref evita recarregar os dados por causa disso
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const defaultPeriodRef = useRef(defaultPeriod);
  defaultPeriodRef.current = defaultPeriod;

  useEffect(() => {
    if (!show) return;
    setPeriod(defaultPeriodRef.current);
    setSelProviders([]);
    setSelStatuses(DEFAULT_STATUSES);
    setSelCondos([]);
  }, [show]);

  const range = useMemo(() => periodRange(period || {}), [period]);

  // Carrega ativações + negativas do período (filtros de parceiro/status/condomínio são locais)
  useEffect(() => {
    if (!show || !range) { setSource([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
        const params = new URLSearchParams({ startDate: range.start.toISOString(), endDate: range.end.toISOString(), period: "range", limit: "5000" });
        const negParams = new URLSearchParams({ startDate: range.start.toISOString(), endDate: range.end.toISOString() });
        const [actResp, negResp] = await Promise.all([
          fetch(`${API_URL}/admin/activations?${params}`, { headers }),
          fetch(`${API_URL}/admin/negatives?${negParams}`, { headers }),
        ]);
        const act = await actResp.json();
        if (!actResp.ok) throw new Error(act?.message || "Falha ao carregar ativações");
        const neg = negResp.ok ? await negResp.json() : { items: [] };
        if (!cancelled) {
          setSource([
            ...(act.items || []),
            ...(neg.items || []).map((n) => ({ ...n, _negativeDoc: true, status: "NEGATIVA" })),
          ]);
        }
      } catch (e) {
        if (!cancelled) { setSource([]); notifyRef.current?.("Erro", e?.message || "Falha ao carregar dados do relatório", "danger"); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [show, range]);

  // Opções com quantidade no período (parceiros: só os que têm ativação no período)
  const providerOptions = useMemo(() => countBy(source, (a) => a.provider).map(([name, count]) => ({
    value: name,
    label: name,
    count,
    color: /^#[0-9a-f]{6}$/i.test(partnerColorMap[name] || "") ? partnerColorMap[name] : T.ink4,
  })), [source, partnerColorMap]);
  const condoOptions = useMemo(() => countBy(source, condoOf).map(([name, count]) => ({ value: name, label: name, count })), [source]);

  const rows = useMemo(() => source
    .map((a) => ({
      raw: a,
      status: reportStatus(a),
      provider: a.provider || "",
      condo: condoOf(a),
    }))
    .filter((r) => selStatuses.includes(r.status))
    .filter((r) => !selProviders.length || selProviders.includes(r.provider))
    .filter((r) => !selCondos.length || selCondos.includes(r.condo))
    .map(({ raw: a, status, provider, condo }) => ({
      status,
      provider,
      condo: [condo, a.unit && `Unid. ${a.unit}`].filter(Boolean).join(" · "),
      client: titleCase(a.clientName || a.personName || ""),
      document: a.maskedDocument || "—",
      plan: a.plan || "",
      valueCents: cents(a.value),
      activation: dateOnly(a.activationDate || a.createdAt),
      installation: dateOnly(a.installationDate),
      sortKey: new Date(a.activationDate || a.createdAt || 0).getTime(),
    }))
    .sort((a, b) => b.sortKey - a.sortKey), [source, selStatuses, selProviders, selCondos]);

  const activeValue = rows.filter((r) => r.status === "ATIVADO" || r.status === "PAGO").reduce((t, r) => t + r.valueCents, 0);

  function generate() {
    if (!rows.length || !range) return;
    // Abre a aba já no clique (evita bloqueio de pop-up) e carrega o PDF nela em seguida
    const tab = window.open("", "_blank");
    if (tab) tab.document.write(`<title>Gerando relatório…</title><p style="font-family:${T.font};color:#86868b;padding:24px">Gerando relatório…</p>`);
    setGenerating(true);
    try {
      const filtersText = [
        `Período: ${range.label}`,
        `Parceiros: ${selProviders.length ? selProviders.join(", ") : "todos"}`,
        `Status: ${REPORT_STATUSES.filter((s) => selStatuses.includes(s.value)).map((s) => s.label).join(", ")}`,
        `Condomínios: ${selCondos.length ? selCondos.join(", ") : "todos"}`,
      ].join("   ·   ");
      let user = "";
      try {
        // e-mail do usuário logado vem do payload do token de acesso
        const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        user = (payload.email || "").split("@")[0];
      } catch (e) { user = ""; }
      const doc = buildPdf({ rows, filtersText, generatedBy: user });
      const url = doc.output("bloburl");
      if (tab) tab.location.href = url;
      else doc.save(`relatorio-ativacoes-${new Date().toISOString().slice(0, 10)}.pdf`);
      onHide();
    } catch (e) {
      if (tab) tab.close();
      notify?.("Erro", "Não foi possível gerar o PDF.", "danger");
    } finally {
      setGenerating(false);
    }
  }

  const label = (text, extra) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.07em" }}>{text}</span>
      {extra}
    </div>
  );
  const chip = (on, onClick, children, color = T.accent, key) => (
    <button key={key} type="button" onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: on ? 600 : 400, cursor: "pointer", fontFamily: T.font, border: `1px solid ${on ? color : T.hair}`, background: on ? `${color}14` : "#fff", color: on ? color : T.ink2, transition: "all .12s" }}>
      {children}
    </button>
  );
  const input = { height: 36, borderRadius: 10, border: `1px solid ${T.hair}`, background: "#fff", fontSize: 13, padding: "0 10px", fontFamily: T.font, color: T.ink };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ borderBottom: `1px solid ${T.hair}`, padding: "16px 22px" }}>
        <div>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            <DocumentArrowDownIcon style={{ width: 20, height: 20, color: T.accent }} /> Relatório em PDF
          </Modal.Title>
          <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>Escolha os filtros. O PDF abre numa nova aba.</div>
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 20, fontFamily: T.font }}>
        {/* Período */}
        <div>
          {label("Período")}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", padding: 3, borderRadius: 10, background: T.surface2, border: `1px solid ${T.hair}` }}>
              {[{ v: "month", l: "Mês" }, { v: "year", l: "Ano" }, { v: "range", l: "Intervalo" }].map((o) => (
                <button key={o.v} type="button" onClick={() => setPeriod((p) => ({ ...p, mode: o.v }))} style={{ padding: "5px 14px", borderRadius: 8, border: "none", fontSize: 12.5, fontWeight: 500, fontFamily: T.font, cursor: "pointer", background: period?.mode === o.v ? "#fff" : "transparent", color: period?.mode === o.v ? T.ink : T.ink3, boxShadow: period?.mode === o.v ? "0 1px 2px rgba(20,20,22,0.06)" : "none" }}>{o.l}</button>
              ))}
            </div>
            {period?.mode === "month" && <input type="month" value={period.month} onChange={(e) => setPeriod((p) => ({ ...p, month: e.target.value }))} style={input} />}
            {period?.mode === "year" && <input type="number" min="2020" max="2100" value={period.year} onChange={(e) => setPeriod((p) => ({ ...p, year: e.target.value }))} style={{ ...input, width: 100 }} />}
            {period?.mode === "range" && (
              <>
                <input type="date" value={period.start} onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))} style={input} />
                <span style={{ fontSize: 12, color: T.ink3 }}>até</span>
                <input type="date" value={period.end} onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))} style={input} />
              </>
            )}
          </div>
        </div>

        {/* Status */}
        <div>
          {label("Status")}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {REPORT_STATUSES.map((s) => chip(selStatuses.includes(s.value), () => setSelStatuses((l) => toggle(l, s.value)), (
              <><span style={{ width: 7, height: 7, borderRadius: "50%", background: s.color }} />{s.label}</>
            ), s.color, s.value))}
          </div>
        </div>

        {/* Parceiros */}
        <div>
          {label("Parceiros")}
          <MultiSelect
            options={providerOptions}
            selected={selProviders}
            onChange={setSelProviders}
            allLabel="Todos os parceiros"
            searchPlaceholder="Buscar parceiro…"
            emptyText="Nenhum parceiro com ativação no período"
            loading={loading}
          />
        </div>

        {/* Condomínios */}
        <div>
          {label("Condomínios")}
          <MultiSelect
            options={condoOptions}
            selected={selCondos}
            onChange={setSelCondos}
            allLabel="Todos os condomínios"
            searchPlaceholder="Buscar condomínio…"
            emptyText="Nenhum condomínio no período"
            loading={loading}
          />
        </div>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: `1px solid ${T.hair}`, padding: "12px 22px", justifyContent: "space-between", fontFamily: T.font }}>
        <div style={{ fontSize: 12.5, color: T.ink3 }}>
          {loading ? "Calculando…" : !range ? "Informe o período" : !selStatuses.length ? "Escolha ao menos um status" : (
            <><strong style={{ color: T.ink }}>{rows.length}</strong> registro{rows.length === 1 ? "" : "s"} · ativo <strong style={{ color: T.ink }}>{money(activeValue)}</strong></>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={onHide} style={{ height: 36, padding: "0 16px", borderRadius: 10, border: `1px solid ${T.hair}`, background: "#fff", color: T.ink2, fontSize: 13, fontFamily: T.font, cursor: "pointer" }}>Cancelar</button>
          <button type="button" onClick={generate} disabled={loading || generating || !rows.length} style={{ height: 36, padding: "0 16px", borderRadius: 10, border: "none", background: T.accent, color: "#fff", fontSize: 13, fontWeight: 500, fontFamily: T.font, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", opacity: loading || generating || !rows.length ? 0.45 : 1 }}>
            {generating ? <Spinner size="sm" animation="border" /> : <><DocumentArrowDownIcon style={{ width: 15, height: 15 }} /> Gerar PDF</>}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
